<template>
  <ModuleWrapper :moduleId="moduleId" title="Tracking Pixel Snippet" @initializeData="initializeData">
    <template #main>
      <div data-testId="splash-tracking-pixel-setup" class="tw-pb-8">
        <div class="tw-mx-auto tw-grid tw-w-auto tw-max-w-[1000px] tw-grid-cols-1 tw-gap-y-10">
          <div class="tw-w-full tw-px-2">
            <CodeEditorWrapper
              :key="isFetchingScript ? 'abc' : 'xyz'"
              v-bind="state"
              :codeAreaKey="codeAreaKey"
              :characterLimit="4096"
              @save="({ onSuccess, onFailure, doc }) => handleSave(onSuccess, onFailure, doc)"
              @edit="isEditing = true"
              @cancel="isEditing = false"
            />
          </div>
        </div>
      </div>
    </template>
  </ModuleWrapper>
</template>

<script lang="ts">
import { createUpdateSplashTrackingPixelSnippet } from '@goldcast/api/backend';
import { defineComponent } from 'vue';
import { mapGetters, mapActions, mapMutations } from 'vuex';

import ModuleWrapper from './ModuleWrapper.vue';

import CodeEditorWrapper from '@/studio/marketingTags/CodeEditorWrapper.vue';
import { METHOD_SEPARATOR } from '@/studio/marketingTags/marketingTags.constants';
import { extractContent, generateDoc } from '@/studio/marketingTags/marketingTags.utils';
import { CodeAreaKeys, CodeAreaConfig } from '@/studio/marketingTags/store/types';

export default defineComponent({
  name: 'TrackingPixelSnippet',
  components: { ModuleWrapper, CodeEditorWrapper },
  props: {
    moduleId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isEditing: false,
      isFetchingScript: true,
      state: {
        description:
          'This function fires when the Event Landing Page (Splash Page) is loaded. Use this to add tracking pixels or other scripts to the page.<br/><i>NOTE: Use javascript to insert html elements if required.</i>',
        docStructure: {
          firstLine: '(function () {',
          content: ' // Replace this with Javascript you want to fire on Event Landing Page (Splash Page).\n\n\n',
          lastLine: '})()'
        },
        language: 'javascript',
        readOnlyRanges: [
          { fromLine: 1, toLine: 1 },
          { fromLine: 'last', toLine: 'last' }
        ],
        doc: '(function () {\n // Replace this with Javascript you want to fire on Event Landing Page (Splash Page).\n\n\n\n})()',
        restrictedKeywords: ['cookie', 'localStorage', 'sessionStorage', METHOD_SEPARATOR]
      } as CodeAreaConfig,
      codeAreaKey: 'body' as CodeAreaKeys
    };
  },
  mounted() {
    this.initializeData();
  },
  computed: {
    ...mapGetters('studioEventList', {
      event: 'getCurrentEvent'
    })
  },
  methods: {
    ...mapActions('studioToast', { triggerToast: 'triggerToast' }),
    ...mapMutations('studioEventList', { updateEvent: 'updateEvent' }),
    initializeData() {
      const snippet = this.event?.splash_tracking_pixel_snippet?.body_section_script;
      if (!snippet) {
        return;
      }
      this.state.docStructure.content = snippet;
      this.state.doc = generateDoc(this.state.docStructure);
      this.isFetchingScript = false;
    },
    handleSave(onSuccess: Function, onFailure: Function, doc: string) {
      const newContent = extractContent(doc);
      const prevContent = this.state.docStructure.content;
      createUpdateSplashTrackingPixelSnippet({
        id: this.event.id,
        body: {
          body_section_script: newContent
        }
      })
        .then(() => {
          onSuccess();
          this.state.docStructure.content = newContent;
          this.updateEvent({
            [this.event.id]: { ...this.event, splash_tracking_pixel_snippet: { body_section_script: newContent } }
          });
        })
        .catch(() => {
          onFailure();
          this.state.docStructure.content = prevContent;
        });
    }
  }
});
</script>
